import { useEffect, useRef, useState } from "react";

/**
 * Live speed + ETA tracker for long-running sync jobs.
 *
 * Sampling model: on every render (usually driven by a 2-6s status poll),
 * push {done, at:Date.now()} onto a rolling buffer. Compute speed from
 * the delta between the newest and oldest sample in the buffer — this
 * naturally windows to ~last 60s and stays smooth even when the poll
 * cadence varies.
 *
 * Returns:
 *   speed  — items/sec over the recent window (rounded to 1 decimal)
 *   etaSec — seconds remaining based on that speed (integer)
 *   etaStr — pre-formatted "3m 15s" style string
 *   ready  — false until we have ≥2 samples spanning ≥3 seconds
 *
 * Reset happens automatically when `done` decreases (e.g. a new sync
 * starts and the counter resets to 0).
 */
export default function useSyncSpeed({ done, total, windowMs = 60000, active = true }) {
  const bufRef = useRef([]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!active || done == null || total == null) return;
    const buf = bufRef.current;
    // Reset if `done` decreased — new run started.
    if (buf.length && done < buf[buf.length - 1].done) {
      bufRef.current = [];
    }
    bufRef.current = [
      ...bufRef.current.filter((s) => Date.now() - s.at <= windowMs),
      { done, at: Date.now() },
    ];
    // Force re-render every ~1s while active so ETA counts down between polls.
    const t = setInterval(() => setTick((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, [done, total, active, windowMs]);

  const buf = bufRef.current;
  if (!active || buf.length < 2) {
    return { speed: 0, etaSec: null, etaStr: "…", ready: false };
  }
  const oldest = buf[0];
  const newest = buf[buf.length - 1];
  const dtSec = (newest.at - oldest.at) / 1000;
  if (dtSec < 3) {
    return { speed: 0, etaSec: null, etaStr: "…", ready: false };
  }
  const speed = (newest.done - oldest.done) / dtSec;
  if (speed <= 0) {
    return { speed: 0, etaSec: null, etaStr: "computing…", ready: false };
  }
  const remaining = Math.max(0, total - newest.done);
  const etaSec = Math.round(remaining / speed);
  return {
    speed: Math.round(speed * 10) / 10,
    etaSec,
    etaStr: formatEta(etaSec),
    ready: true,
    // Suppress the tick-var-unused warning — value is only used to
    // trigger the setInterval-driven re-render.
    _tick: tick,
  };
}

function formatEta(sec) {
  if (sec == null) return "…";
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}h ${mm}m`;
}
